import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext({
  theme: 'light',
  setTheme: () => {},
  toggleTheme: () => {},
});

// Light is the Design System v1 default; dark is opt-in via the toggle and
// persisted in localStorage. Only an explicit 'dark' preference is honoured.
function readStoredTheme() {
  if (typeof window === 'undefined') return 'light';
  try {
    return window.localStorage.getItem('ga_theme') === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export const ThemeProvider = ({ children }) => {
  // Lazy init avoids a race where a second effect ran with default "dark" and overwrote
  // a saved "light" preference in localStorage before hydration finished (worse under production timing).
  const [theme, setTheme] = useState(() => readStoredTheme());

  // Keep <html class="theme-light">, localStorage, and browser chrome hint in sync
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('theme-light', theme === 'light');
    try {
      window.localStorage.setItem('ga_theme', theme);
    } catch {
      // ignore (private mode, etc.)
    }
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute('content', theme === 'light' ? '#f0fdf4' : '#0F172A');
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

